import React, { FC, useState } from 'react';
import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import { ReactComponent as DeleteIcon } from 'assets/icons/remove-modal.svg';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import LocalModal from 'components/base/local-modal';
import useDashboardContext from 'contexts/dashboard.context';
import { formatCurrency, getFullName, handleApiError } from 'utils/functions';
import { EventGuestType } from 'models/guest.model';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';

interface DeleteGuestProps {
  show: boolean;
  onClose: (withRes?: boolean) => void;
  selectedGuest: EventGuestType[];
  setSelectedGuest: (value: EventGuestType[]) => void;
  removeGuests: (ids: string[]) => void;
}

interface DeleteGuestFooterProps {
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const DeleteGuestFooter: FC<DeleteGuestFooterProps> = ({ onCancel, onConfirm, loading }) => (
  <div className='grid grid-cols-2 gap-x-4'>
    <Button small color='primary' variant='outline' className='mx-auto' onClick={onCancel}>
      Cancel
    </Button>
    <Button
      small
      color='primary'
      variant='block'
      className='mx-auto'
      loading={loading}
      onClick={onConfirm}>
      Confirm
    </Button>
  </div>
);

const DeleteGuest: FC<DeleteGuestProps> = ({
  show,
  onClose,
  selectedGuest,
  setSelectedGuest,
  removeGuests
}) => {
  const [loading, setLoading] = useState(false);
  const { activeCompany } = useDashboardContext();
  const companyId: string = activeCompany?.id;
  const confirmBulkDelete = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const guest_ids = selectedGuest?.map((item) => item.id);
      apiService.post('guest/delete', {
        data: {
          guest_ids
        }
      });

      toast(<ToastMessage type='success' message='Guest(s) deleted successfully' />);
      onClose();
      setSelectedGuest?.([]);
      if (guest_ids) removeGuests(guest_ids);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };
  const confirmSingleDelete = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await apiService.delete(`guest/${selectedGuest[0]?.id}`);
      if (res.status === 200) {
        toast(<ToastMessage type='success' message='Guest deleted successfully' />);
        if (selectedGuest[0]?.id) removeGuests([selectedGuest[0]?.id]);
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
      onClose();
    }
  };
  const user = selectedGuest[0];
  return (
    <LocalModal
      isOpen={show}
      title=''
      titleIconColor='danger'
      onRequestClose={() => onClose()}
      footer={
        <DeleteGuestFooter
          loading={loading}
          onCancel={() => onClose()}
          onConfirm={() => {
            if (selectedGuest?.length > 1) {
              confirmBulkDelete();
            } else {
              confirmSingleDelete();
            }
          }}
        />
      }>
      {selectedGuest && selectedGuest?.length > 1 ? (
        <>
          <div className='flex flex-col justify-center items-center'>
            <DeleteIcon className='text-red-5 mx-auto mb-4' />
            <p className='text-[#2E3031] text-center mb-1'>Delete selected persons</p>
            <p className='text-[#6F7376] text-center'>
              This will delete all info associated with these persons
            </p>
          </div>
        </>
      ) : (
        <div className=''>
          <DeleteIcon className='text-red-5 mx-auto mb-4' />
          <p className='text-[#2E3031] text-center mb-1'>Are you sure you want to remove</p>
          <p className='text-[#2E3031] text-center mb-1 capitalize'>
            {getFullName(user, 'this user')}
          </p>
          {Number(selectedGuest[0]?.balance) > 0 && (
            <div className='bg-[#FFF4E5] flex items-center gap-[6px] rounded-[6px] py-2 px-4 mt-5'>
              <InfoIcon className='shrink-0' />
              <p className='text-[#B26800] text-xs'>
                Event guest balance of {formatCurrency(Number(selectedGuest[0]?.balance))} will be
                refunded to the event wallet.
              </p>
            </div>
          )}
        </div>
      )}
    </LocalModal>
  );
};

export default DeleteGuest;
